export interface TrayQuery<Variables> {
  formatQuery(variables: Variables): string;
}

export type GetVariables<C extends TrayQuery<unknown>> = C extends TrayQuery<
  infer Variables
>
  ? Variables
  : unknown;

function makeQuery<Variables>(query: string): TrayQuery<Variables> {
  return new (class implements TrayQuery<Variables> {
    formatQuery(variables: Variables): string {
      return JSON_stringify({
        query,
        variables,
      });
    }
  })();
}

function JSON_stringify(o: unknown, emitUnicode = true): string {
  const json = JSON.stringify(o);
  return emitUnicode
    ? json
    : json.replace(/[\u007f-\uffff]/g, function (c) {
        return '\\u' + ('0000' + c.charCodeAt(0).toString(16)).slice(-4);
      });
}

export const getAuthentications = makeQuery<Record<string, never>>(
  `query {
    viewer {
      authentications {
        edges {
          node {
            id
            customFields
            name
            service {
              id
              name
              version
              title
              icon
            }
          }
        }
      }
    }
  }`,
);

export const getSolutionInstance = makeQuery<{
  solutionId: string;
  solutionInstanceId: string;
}>(
  `query(
    $solutionId: String!,
    $solutionInstanceId: String!
  ) {
    viewer {
      solutionInstances(criteria: { solutionId: $solutionId, id: $solutionInstanceId }) {
        edges {
          node {
            id
            name
            enabled
            owner
            configValues {
              externalId
              value
            }
            authValues {
              externalId
              authId
            }
            solutionVersionFlags {
              hasNewerVersion
            }
            workflows {
              edges {
                node {
                  id
                  sourceWorkflowName
                  triggerUrl
                }
              }
            }
          }
        }
      }
    }
  }`,
);

export const getUsers = makeQuery<{
  criteria?: {
    externalUserId?: string;
    name?: string;
    userId?: string;
    isTestUser?: boolean;
  };
  first?: number;
  last?: number;
  before?: string;
  after?: string;
}>(
  `query(
    $criteria: UserSearchCriteria,
    $first: Int,
    $last: Int,
    $before: String,
    $after: String
  ) {
    users(criteria: $criteria, first: $first, last: $last, before: $before, after: $after) {
      edges {
        node {
          id
          name
          externalUserId
          isTestUser
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }`,
);

export const getSolutions = makeQuery<{
  criteria?: { tags?: string[] };
  first?: number;
  last?: number;
  before?: string;
  after?: string;
}>(
  `query(
    $criteria: SolutionSearchCriteria,
    $first: Int,
    $last: Int,
    $before: String,
    $after: String
  ) {
    viewer {
      solutions(criteria: $criteria, first: $first, last: $last, before: $before, after: $after) {
        edges {
          node {
            id
            title
            description
            tags
            customFields {
              key
              value
            }
            configSlots {
              externalId
              title
              defaultValue
            }
            authSlots {
              externalId
              title
            }
          }
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
      }
    }
  }`,
);

export const getSolutionInstances = makeQuery<{
  criteria?: {
    id?: string;
    name?: string;
    owner?: string;
    solutionId?: string;
  };
  first?: number;
  last?: number;
  before?: string;
  after?: string;
}>(
  `query(
    $criteria: SolutionInstanceSearchCriteria,
    $first: Int,
    $last: Int,
    $before: String,
    $after: String
  ) {
    viewer {
      solutionInstances(criteria: $criteria, first: $first, last: $last, before: $before, after: $after) {
        edges {
          node {
            id
            name
            enabled
            owner
            configValues {
              externalId
              value
            }
            authValues {
              externalId
              authId
            }
            solutionVersionFlags {
              hasNewerVersion
              requiresUserInputToUpdateVersion
              requiresSystemInputToUpdateVersion
            }
            workflows {
              edges {
                node {
                  id
                  sourceWorkflowName
                  triggerUrl
                }
              }
            }
          }
        }
      }
    }
  }`,
);

export const getViewerDetails = makeQuery<Record<string, never>>(
  `query {
    viewer {
      details {
        username
        email
        name
      }
    }
  }`,
);

export const updateSolutionInstance = makeQuery<{
  input: {
    solutionInstanceId: string;
    instanceName?: string;
    enabled?: boolean;
    authValues?: { externalId: string; authId: string }[];
    configValues?: { externalId: string; value: string }[];
    errorOnEnablingWithMissingValues?: boolean;
    clientMutationId?: string;
  };
}>(
  `mutation($input: UpdateSolutionInstanceInput!) {
    updateSolutionInstance(input: $input) {
      solutionInstance {
        id
        name
        enabled
      }
      clientMutationId
    }
  }`,
);

export const createUserAuthentication = makeQuery<{
  input: {
    name: string;
    serviceId: string;
    serviceEnvironmentId: string;
    data: any;
    formData?: any;
    scopes?: string[];
    hidden?: boolean;
    clientMutationId?: string;
  };
}>(
  `mutation($input: CreateUserAuthenticationInput!) {
    createUserAuthentication(input: $input) {
      authenticationId
      clientMutationId
    }
  }`,
);

export const removeAuthentication = makeQuery<{
  input: {
    authenticationId: string;
    clientMutationId?: string;
  };
}>(
  `mutation($input: RemoveUserAuthenticationInput!) {
    removeAuthentication(input: $input) {
      clientMutationId
    }
  }`,
);

export const updateUserAuthentication = makeQuery<{
  input: {
    authenticationId: string;
    name: string;
    clientMutationId?: string;
  };
}>(
  `mutation UpdateUserAuthentication($input: UpdateUserAuthenticationInput!) {
    updateUserAuthentication(input: $input) {
      authentication {
        id
        name
      }
    }
  }`,
);

export const callConnector = makeQuery<{
  connector: string;
  version?: string;
  operation: string;
  authId: string;
  input: string;
}>(
  `mutation(
    $connector: String!,
    $version: String,
    $operation: String!,
    $authId: String,
    $input: Json!
  ) {
    callConnector(input: {
      connector: $connector,
      version: $version,
      operation: $operation,
      authId: $authId,
      input: $input
    }) {
      output
    }
  }`,
);
